"use client";
import type { SMBPaginatedRequest } from "@layer/types/api";
import type {
	InfiniteData,
	UseInfiniteQueryResult,
} from "@tanstack/react-query";
import type { OnChangeFn, PaginationState } from "@tanstack/react-table";
import { useEffect, useState } from "react";
import { flattenPaginatedPages } from "../helpers";

type UsePaginatedTableDataOptions<T> = {
	queryResult: UseInfiniteQueryResult<
		InfiniteData<SMBPaginatedRequest<T> | undefined>,
		unknown
	>;
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
};

export type UsePaginatedTableDataResult<T> = {
	pageSize: number;
	setPageSize: React.Dispatch<React.SetStateAction<number>>;
	hasNextPage: boolean;
	pageIndex: number;
	setPageIndex: React.Dispatch<React.SetStateAction<number>>;
	handlePaginationChange: OnChangeFn<PaginationState>;
	currentPageData: T[];
	isLoading: boolean;
	isPending: boolean;
	data: InfiniteData<SMBPaginatedRequest<T> | undefined> | undefined;
	error: unknown;
};

const usePaginatedTableData = <T>({
	queryResult,
	pageSize,
	setPageSize,
}: UsePaginatedTableDataOptions<T>): UsePaginatedTableDataResult<T> => {
	const [pageIndex, setPageIndex] = useState(0);

	const {
		data,
		isLoading,
		isPending,
		fetchNextPage,
		hasNextPage,
		isFetchingNextPage,
		error,
	} = queryResult;

	const handlePaginationChange: OnChangeFn<PaginationState> = (
		updaterOrValue,
	) => {
		const newPagination =
			typeof updaterOrValue === "function"
				? updaterOrValue({ pageIndex, pageSize })
				: updaterOrValue;

		setPageIndex(newPagination.pageIndex);
		setPageSize(newPagination.pageSize);
	};

	useEffect(() => {
		if (
			pageIndex + 1 >= (data?.pages?.length ?? 0) &&
			hasNextPage &&
			!isFetchingNextPage
		) {
			fetchNextPage();
		}
	}, [
		pageIndex,
		pageSize,
		data,
		hasNextPage,
		isFetchingNextPage,
		fetchNextPage,
	]);

	const flattenedData = flattenPaginatedPages(data);

	const tableHasNextPage = flattenedData.length > (pageIndex + 1) * pageSize;

	const currentPageData = flattenedData.slice(
		pageIndex * pageSize,
		(pageIndex + 1) * pageSize,
	);

	return {
		pageSize,
		setPageSize,
		hasNextPage: tableHasNextPage,
		pageIndex,
		setPageIndex,
		handlePaginationChange,
		currentPageData,
		isLoading,
		isPending,
		data,
		error,
	};
};

export default usePaginatedTableData;
